import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

type RequiredRole = "admin" | "owner" | "partner" | "trainer" | "authenticated";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: RequiredRole | RequiredRole[];
  redirectTo?: string;
}

/**
 * ProtectedRoute component that validates authentication and authorization
 * before rendering protected content.
 * 
 * Security: This adds defense-in-depth at the route level. 
 * Primary security is still enforced via RLS policies at the database level.
 */
const ProtectedRoute = ({ 
  children, 
  requiredRole, 
  redirectTo 
}: ProtectedRouteProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [redirect, setRedirect] = useState<string | null>(null);

  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setRedirect(redirectTo || "/login");
          setIsLoading(false);
          return;
        }

        // For basic authenticated access, session is sufficient
        const rolesNeeded = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
        if (rolesNeeded.includes("authenticated")) {
          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }

        // Check role from user_roles table (server-side validation via RLS)
        const { data: roles, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id);

        if (error) {
          console.error("Error checking user roles:", error);
          setRedirect(redirectTo || "/login");
          setIsLoading(false);
          return;
        }

        const userRoles = roles?.map(r => r.role) || [];
        const hasAccess = rolesNeeded.some(r => userRoles.includes(r as any));

        if (hasAccess) {
          setIsAuthorized(true);
        } else {
          const primary = rolesNeeded[0];
          const defaultRedirect = primary === "admin" ? "/admin/login" : primary === "partner" ? "/partner/login" : primary === "trainer" ? "/trainer/login" : primary === "owner" ? "/owner/login" : "/login";
          setRedirect(redirectTo || defaultRedirect);
        }
      } catch (error) {
        console.error("Authorization check failed:", error);
        setRedirect(redirectTo || "/login");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthorization();
  }, [requiredRole, redirectTo]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  if (!isAuthorized) {
    return <Navigate to={redirectTo || "/login"} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
